'use client'
import { useState, useEffect, useRef, useCallback } from 'react'
import { Box, Typography, styled, keyframes, Popover, Slider, Checkbox } from '@mui/material'
import { TuneRounded } from '@mui/icons-material'
import apiService from '../../services/ApiService'
import { countryMeta, countryOrder } from '../../services/newsConfig'
import {
  isLocalDevelopment,
  isCountrySpecific,
  getCountryFromDomain,
} from '../../utils/domainUtils'

const formatShortDate = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(d)
}

/** Derive which country slugs this domain should show.
 * Returns 'all' for GCC/local domains (uses single bulk endpoint), or a single-element array for country-specific domains.
 */
const getSlugsForDomain = () => {
  const hostname = window.location.hostname
  if (!isLocalDevelopment(hostname) && isCountrySpecific(hostname)) {
    const countryName = getCountryFromDomain(hostname) // e.g. 'UAE'
    const matched = countryOrder.find(
      (slug) => countryMeta[slug].name === countryName,
    )
    return matched ? [matched] : countryOrder
  }
  // local dev or main GCC domain → use single bulk endpoint
  return 'all'
}

/** Find the display slug for a ticker item.
 * Prefers `_tickerSlug` stamped during data load (= the API section it came from),
 * then falls back to scanning the `countries[]` / `country` field.
 */
const getSlugForItem = (item) => {
  if (item._tickerSlug) return item._tickerSlug
  const countries = item.countries || []
  for (const slug of countryOrder) {
    const full = countryMeta[slug].fullName.toLowerCase()
    if (countries.some(c => {
      const cl = c.toLowerCase()
      return cl === full || full.includes(cl) || cl.includes(full)
    })) return slug
  }
  // fallback: try matching item.country string
  const single = (item.country || '').toLowerCase()
  if (!single) return null
  return (
    countryOrder.find((s) => {
      const m = countryMeta[s]
      const fn = m.fullName.toLowerCase()
      const nm = m.name.toLowerCase()
      return fn === single || nm === single || fn.includes(single) || single.includes(fn)
    }) || null
  )
}

// scroll & speed constants
const STEP_PX = 200       // pixels shifted per arrow click
const RESUME_DELAY = 1500 // ms before auto-scroll resumes after arrow press
const LS_SPEED_KEY = 'gcctaxnews_ticker_speed'
const DEFAULT_SPEED = 70
const MIN_SPEED = 15
const MAX_SPEED = 120

// Ticker country display order
const TICKER_COUNTRY_ORDER = ['bahrain', 'ksa', 'kuwait', 'oman', 'qatar', 'uae']
const LS_COUNTRIES_KEY = 'gcctaxnews_ticker_countries'

const readStoredSpeed = () => {
  try {
    const v = localStorage.getItem(LS_SPEED_KEY)
    if (v !== null) {
      const n = Number(v)
      if (!isNaN(n) && n >= MIN_SPEED && n <= MAX_SPEED) return n
    }
  } catch { /* ignore */ }
  return DEFAULT_SPEED
}

const readStoredCountries = () => {
  try {
    const v = localStorage.getItem(LS_COUNTRIES_KEY)
    if (v !== null) {
      const arr = JSON.parse(v)
      if (Array.isArray(arr) && arr.length > 0) return arr.filter(c => TICKER_COUNTRY_ORDER.includes(c))
    }
  } catch { /* ignore */ }
  return [...TICKER_COUNTRY_ORDER]
}

// styled pieces
const TickerWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  backgroundColor: '#1a1c22',
  borderBottom: '1px solid rgba(255, 218, 27, 0.18)',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'stretch',
  height: 36,
  userSelect: 'none',
  position: 'sticky',
  top: 64,
  zIndex: theme.zIndex.appBar - 1,
  '@media (max-width:600px)': {
    top: 56,
  },
}))

const Label = styled(Box)({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '0 14px',
  backgroundColor: '#ffda1b',
  color: '#1a1c22',
  fontWeight: 700,
  fontSize: '0.72rem',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  borderRight: '2px solid rgba(0,0,0,0.15)',
})

const TrackOuter = styled(Box)({
  flex: 1,
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
})

const Track = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  willChange: 'transform',
})

const Separator = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  margin: '0 20px',
  color: 'rgba(255, 218, 27, 0.4)',
  fontSize: '0.8rem',
})

const ArrowBtn = styled('button')({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 30,
  height: '100%',
  backgroundColor: 'transparent',
  border: 'none',
  borderLeft: '1px solid rgba(255,218,27,0.15)',
  color: 'rgba(255,218,27,0.6)',
  cursor: 'pointer',
  fontSize: '1.2rem',
  lineHeight: 1,
  padding: 0,
  transition: 'background 0.18s, color 0.18s',
  '&:hover': {
    backgroundColor: 'rgba(255,218,27,0.12)',
    color: '#ffda1b',
  },
  '&:active': {
    backgroundColor: 'rgba(255,218,27,0.22)',
  },
})

const SettingsBtn = styled('button')({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: '100%',
  backgroundColor: 'transparent',
  border: 'none',
  borderLeft: '1px solid rgba(255,218,27,0.15)',
  color: 'rgba(255,218,27,0.5)',
  cursor: 'pointer',
  padding: 0,
  transition: 'background 0.18s, color 0.18s',
  '&:hover': {
    backgroundColor: 'rgba(255,218,27,0.1)',
    color: '#ffda1b',
  },
  '&:active': {
    backgroundColor: 'rgba(255,218,27,0.2)',
  },
})

// single ticker item
const TickerItem = ({ item, onClick }) => {
  const slug = getSlugForItem(item)
  const meta = slug ? countryMeta[slug] : null
  const dateStr = formatShortDate(item.pubDate)

  return (
    <Box
      component='span'
      onClick={() => onClick(item, slug)}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        cursor: 'pointer',
        px: 0.5,
        py: 0.25,
        borderRadius: '4px',
        transition: 'background 0.2s ease',
        '&:hover': {
          backgroundColor: 'rgba(255, 218, 27, 0.08)',
        },
      }}
    >
      {/* Country flag + name */}
      {meta && (
        <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
          <Box
            component='img'
            src={`https://flagcdn.com/w20/${meta.flagCode}.png`}
            alt={meta.name}
            sx={{ width: 16, height: 11, objectFit: 'cover', borderRadius: '2px', display: 'inline-block', verticalAlign: 'middle', flexShrink: 0, border: '1px solid rgba(255,255,255,0.25)' }}
          />
          <Typography
            component='span'
            sx={{ fontSize: '0.73rem', fontWeight: 700, color: '#ffda1b', letterSpacing: '0.04em' }}
          >
            {meta.name}
          </Typography>
        </Box>
      )}

      {/* Divider dot */}
      <Typography component='span' sx={{ color: 'rgba(255,218,27,0.35)', fontSize: '0.7rem' }}>
        ›
      </Typography>

      {/* Title */}
      <Typography
        component='span'
        sx={{
          fontSize: '0.8rem',
          fontWeight: 500,
          color: 'rgba(255, 255, 255, 0.88)',
          letterSpacing: '0.01em',
        }}
      >
        {item.title}
      </Typography>

      {/* Source */}
      {item.source && (
        <Typography
          component='span'
          sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', flexShrink: 0 }}
        >
          · {item.source}
        </Typography>
      )}

      {/* Date */}
      {dateStr && (
        <Typography
          component='span'
          sx={{ fontSize: '0.7rem', color: 'rgba(255,218,27,0.55)', flexShrink: 0 }}
        >
          · {dateStr}
        </Typography>
      )}
    </Box>
  )
}

// main component
const NewsTicker = () => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [speed, setSpeed] = useState(readStoredSpeed)
  const [settingsAnchor, setSettingsAnchor] = useState(null)
  const [selectedCountries, setSelectedCountries] = useState(readStoredCountries)

  const trackRef = useRef(null)
  const offsetRef = useRef(0)           // current pixel offset from left
  const lastTimeRef = useRef(null)      // RAF timestamp - null means "just resumed"
  const rafRef = useRef(null)           // requestAnimationFrame id
  const resumeTimerRef = useRef(null)   // setTimeout id for auto-resume
  const hoverPausedRef = useRef(false)  // paused by mouse hover
  const manualPausedRef = useRef(false) // paused by arrow button or settings
  const speedRef = useRef(readStoredSpeed())  // always-current speed for RAF loop

  // ── data loading ──────────────────────────────────────────────────────────
  useEffect(() => {
    const slugs = getSlugsForDomain()
    let cancelled = false

    const load = async () => {
      try {
        let allItems = []

        if (slugs === 'all') {
          const result = await apiService.fetchCountryNews({ country: 'all' })
          if (cancelled) return
          if (Array.isArray(result.countries)) {
            // Stamp each item with _tickerSlug = the section it came from in the API response
            result.countries.forEach(c => {
              const sectionSlug = c.country // e.g. 'oman', 'uae', 'ksa'
              ;(c.items || []).forEach(item => allItems.push({ ...item, _tickerSlug: sectionSlug }))
            })
          } else {
            allItems = result.items || []
          }
        } else {
          const results = await Promise.allSettled(
            slugs.map((slug) =>
              apiService.fetchCountryNews({ country: slug, page: 1, pageSize: 10 }),
            ),
          )
          if (cancelled) return
          results.forEach((r) => {
            if (r.status === 'fulfilled') allItems.push(...(r.value.items || []))
          })
          // Group by country in ticker order, sort by date within each group
          allItems.sort((a, b) => {
            const slugA = getSlugForItem(a)
            const slugB = getSlugForItem(b)
            const orderA = slugA ? TICKER_COUNTRY_ORDER.indexOf(slugA) : TICKER_COUNTRY_ORDER.length
            const orderB = slugB ? TICKER_COUNTRY_ORDER.indexOf(slugB) : TICKER_COUNTRY_ORDER.length
            if (orderA !== orderB) return orderA - orderB
            const da = a.pubDate ? new Date(a.pubDate) : 0
            const db = b.pubDate ? new Date(b.pubDate) : 0
            return db - da
          })
        }

        setItems(allItems)
      } catch {
        // silently fail - ticker is non-critical
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [])

  // ── RAF scroll loop ────────────────────────────────────────────────────────
  const startLoop = useCallback(() => {
    cancelAnimationFrame(rafRef.current)

    const tick = (timestamp) => {
      if (!trackRef.current) return

      if (!hoverPausedRef.current && !manualPausedRef.current) {
        if (lastTimeRef.current === null) {
          // First frame after resume - record time, don't move (avoids jump)
          lastTimeRef.current = timestamp
        } else {
          const elapsed = (timestamp - lastTimeRef.current) / 1000
          lastTimeRef.current = timestamp
          const halfWidth = trackRef.current.scrollWidth / 2
          if (halfWidth > 0) {
            offsetRef.current = (offsetRef.current + speedRef.current * elapsed) % halfWidth
            trackRef.current.style.transform = `translateX(-${offsetRef.current}px)`
          }
        }
      } else {
        // Paused - reset so there is no time-gap jump when we resume
        lastTimeRef.current = null
      }

      rafRef.current = requestAnimationFrame(tick)
    }

    rafRef.current = requestAnimationFrame(tick)
  }, [])

  useEffect(() => {
    if (!loading && items.length > 0) startLoop()
    return () => {
      cancelAnimationFrame(rafRef.current)
      clearTimeout(resumeTimerRef.current)
    }
  }, [loading, items, startLoop])

  // ── arrow navigation ───────────────────────────────────────────────────────
  const handleArrow = (direction) => {
    if (!trackRef.current) return
    manualPausedRef.current = true
    lastTimeRef.current = null
    clearTimeout(resumeTimerRef.current)

    const halfWidth = trackRef.current.scrollWidth / 2
    if (halfWidth <= 0) return

    let newOffset
    if (direction === 'left') {
      // Clamp at 0 - stop at the first item, never wrap to the end
      newOffset = Math.max(0, offsetRef.current - STEP_PX)
    } else {
      // Right: advance and wrap seamlessly within the duplicated content
      newOffset = (offsetRef.current + STEP_PX) % halfWidth
    }
    offsetRef.current = newOffset

    // Smooth CSS transition for the manual jump
    trackRef.current.style.transition = 'transform 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
    trackRef.current.style.transform = `translateX(-${newOffset}px)`
    setTimeout(() => {
      if (trackRef.current) trackRef.current.style.transition = ''
    }, 460)

    // Auto-resume after RESUME_DELAY ms
    resumeTimerRef.current = setTimeout(() => {
      manualPausedRef.current = false
      lastTimeRef.current = null
    }, RESUME_DELAY)
  }

  const handleClick = (item) => {
    if (item.link) window.open(item.link, '_blank', 'noopener,noreferrer')
  }

  const handleSpeedChange = (_, value) => {
    setSpeed(value)
    speedRef.current = value
    hoverPausedRef.current = false
    try { localStorage.setItem(LS_SPEED_KEY, String(value)) } catch { /* ignore */ }
  }

  const openSettings = (e) => {
    hoverPausedRef.current = false
    lastTimeRef.current = null
    setSettingsAnchor(e.currentTarget)
  }

  const closeSettings = () => {
    setSettingsAnchor(null)
  }

  const handleCountryToggle = (slug) => {
    setSelectedCountries(prev => {
      const next = prev.includes(slug)
        ? prev.filter(s => s !== slug)
        : [...prev, slug]
      if (next.length === 0) return prev
      try { localStorage.setItem(LS_COUNTRIES_KEY, JSON.stringify(next)) } catch { /* ignore */ }
      // Reset scroll position when filter changes
      offsetRef.current = 0
      if (trackRef.current) trackRef.current.style.transform = 'translateX(0px)'
      return next
    })
  }

  if (loading || items.length === 0) return null

  const hostname = window.location.hostname
  const isCountryDomain = !isLocalDevelopment(hostname) && isCountrySpecific(hostname)

  // Compute per-country counts — count item under its primary section only
  const countryCounts = {}
  for (const slug of TICKER_COUNTRY_ORDER) {
    countryCounts[slug] = items.filter(item => getSlugForItem(item) === slug).length
  }

  // Filter by selected countries — match only the item's primary section slug
  const filtered = selectedCountries.length === TICKER_COUNTRY_ORDER.length
    ? items
    : items.filter(item => selectedCountries.includes(getSlugForItem(item)))

  if (filtered.length === 0) return null

  // Duplicate items so the scroll loop is seamless
  const doubled = [...filtered, ...filtered]

  return (
    <TickerWrapper
      onMouseEnter={() => { if (!settingsAnchor) hoverPausedRef.current = true }}
      onMouseLeave={() => { hoverPausedRef.current = false }}
    >
      {/* "Tax News" label */}
      <Label>
        <Box
          component='span'
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            backgroundColor: '#e53e3e',
            display: 'inline-block',
            textTransform: 'none',
            animation: `${keyframes`0%,100%{opacity:1}50%{opacity:0.2}`} 1.4s ease-in-out infinite`,
          }}
        />
        <Typography
          component='span'
          sx={{
            fontSize: '0.8rem',
            fontWeight: 700,
            color: 'rgba(5, 0, 0, 0.88)',
            letterSpacing: '0.01em',
            textTransform: 'none',
          }}
        >
          Tax News
        </Typography>
      </Label>

      {/* Left arrow - scroll backward */}
      <ArrowBtn onClick={() => handleArrow('left')} aria-label='Scroll news backward'>
        ‹
      </ArrowBtn>

      <TrackOuter>
        <Track ref={trackRef}>
          {doubled.map((item, i) => (
            <Box component='span' key={`${item.id || i}-${i}`} sx={{ display: 'inline-flex', alignItems: 'center' }}>
              <TickerItem item={item} onClick={handleClick} />
              <Separator component='span'>|</Separator>
            </Box>
          ))}
        </Track>
      </TrackOuter>

      {/* Right arrow - scroll forward */}
      <ArrowBtn onClick={() => handleArrow('right')} aria-label='Scroll news forward'>
        ›
      </ArrowBtn>

      {/* Speed settings button */}
      <SettingsBtn onClick={openSettings} aria-label='Ticker speed settings'>
        <TuneRounded sx={{ fontSize: 13 }} />
      </SettingsBtn>

      {/* Settings popover */}
      <Popover
        open={Boolean(settingsAnchor)}
        anchorEl={settingsAnchor}
        onClose={closeSettings}
        disableScrollLock
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            backgroundColor: '#1a1c22',
            border: '1px solid rgba(255,218,27,0.22)',
            borderRadius: '8px',
            p: 1,
            width: 145,
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
            mt: 0.5,
          },
        }}
      >
        <Box>
          {/* Scroll Speed */}
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0 }}>
            Scroll Speed
          </Typography>
          <Box sx={{ px: 0.5, maxWidth: 120 }}>
          <Slider
            value={speed}
            min={MIN_SPEED}
            max={MAX_SPEED}
            step={5}
            onChange={handleSpeedChange}
            marks={[
              { value: MIN_SPEED },
              { value: 67 },
              { value: MAX_SPEED },
            ]}
            sx={{
              color: '#ffda1b',
              mb: 0,
              py: '5px',
              '& .MuiSlider-markLabel': { display: 'none' },
              '& .MuiSlider-mark': { backgroundColor: 'rgba(255,255,255,0.25)', width: 3, height: 3 },
              '& .MuiSlider-markActive': { backgroundColor: '#ffda1b' },
              '& .MuiSlider-thumb': {
                width: 13,
                height: 13,
                '&:hover, &.Mui-focusVisible': {
                  boxShadow: '0 0 0 5px rgba(255,218,27,0.15)',
                },
              },
              '& .MuiSlider-track': { height: 3 },
              '& .MuiSlider-rail': { height: 3, backgroundColor: 'rgba(255,255,255,0.12)' },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: '-3px', px: 0.25, mb: 0.5 }}>
            {['Slow', 'Normal', 'Fast'].map((label) => (
              <Typography
                key={label}
                sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1 }}
              >
                {label}
              </Typography>
            ))}
          </Box>
          </Box>

          {/* Country Filter - hidden on single-country domains */}
          {!isCountryDomain && <Box sx={{ borderTop: '1px solid rgba(255,218,27,0.12)', pt: 0.5 }}>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.25 }}>
              Filter Countries
            </Typography>
            {TICKER_COUNTRY_ORDER.map((slug) => {
              const meta = countryMeta[slug]
              const count = countryCounts[slug] || 0
              return (
                <Box
                  key={slug}
                  onClick={() => handleCountryToggle(slug)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    cursor: 'pointer',
                    py: '2px',
                    px: 0.25,
                    borderRadius: '4px',
                    '&:hover': { backgroundColor: 'rgba(255,218,27,0.06)' },
                  }}
                >
                  <Checkbox
                    checked={selectedCountries.includes(slug)}
                    size='small'
                    sx={{
                      p: '2px',
                      color: 'rgba(255,255,255,0.3)',
                      '&.Mui-checked': { color: '#ffda1b' },
                      '& .MuiSvgIcon-root': { fontSize: '0.9rem' },
                    }}
                  />
                  <Box
                    component='img'
                    src={`https://flagcdn.com/w20/${meta.flagCode}.png`}
                    alt={meta.name}
                    sx={{ width: 14, height: 10, objectFit: 'cover', borderRadius: '1px', border: '1px solid rgba(255,255,255,0.2)', flexShrink: 0 }}
                  />
                  <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.75)' }}>
                    {meta.name} <Box component='span' sx={{ color: 'rgba(255,218,27,0.5)', fontSize: '0.65rem' }}>({count})</Box>
                  </Typography>
                </Box>
              )
            })}
          </Box>}
        </Box>
      </Popover>
    </TickerWrapper>
  )
}

export default NewsTicker
